import { useAppStore } from '../store/appStore';

export interface ThemeColors {
  // App shell
  centerPaneBg:        string;
  leftPaneBg:          string;
  rightPaneBg:         string;

  // TopBar
  topBarBg:            string;
  topBarIcon:          string;

  // Cards / Verse cards
  cardBg:              string;
  cardHasFootnotes:    string; // right border color

  // Chapter list
  chapterItemBg:       string;
  chapterNameText:     string;
  chapterTransText:    string;
  selectedChapterBg:   string;
  selectedChapterText: string;
  verseCountBg:        string;
  verseCountText:      string;

  // Text
  primaryText:         string;
  secondaryText:       string;
  accentText:          string; // links, headings (#16A085 / #ECF0F1)
  faIconColor:         string;

  // Verse content
  optionDotsColor:     string;
  hrColor:             string;
  transNameColor:      string;
  transTextColor:      string;
  selectedVerseBg:     string;
  arabicTextColor:     string;

  // Footer
  footerBg:            string;
  footerText:          string;
  footerBorderColor:   string;

  // Settings panel
  settingsSeparator:   string;
  accordionHeader:     string;
  accordionBorder:     string;
  settingsLabelText:   string;
  switchTrackActive:   string;
  switchThumb:         string;

  // RecitationPlayer
  recitationBg:        string;
  repeatBadgeBg:       string;
  recitationCtrlIcon:  string;
  seekTrackBg:         string;
  seekFillBg:          string;
  playBtnBg:           string;

  // Reading position indicator
  readingIndicator:    string;

  // Interactive controls (switch knob, slider fill)
  interactiveColor:    string;
  sliderTrackBg:       string;
  switchThumbInactive: string;

  // Picker / modal
  selectedOptionBg:    string;

  // Misc
  overlayBg:           string;
  glowText:            string;
  inputBg:             string;
}

export const LightColors: ThemeColors = {
  centerPaneBg:        '#e5e5e5',
  leftPaneBg:          '#e5e5e5',
  rightPaneBg:         '#ffffff',

  topBarBg:            '#1ABC9C',
  topBarIcon:          '#2C3E50',

  cardBg:              '#ffffff',
  cardHasFootnotes:    '#FBB917',

  chapterItemBg:       '#ffffff',
  chapterNameText:     '#333333',
  chapterTransText:    '#777777',
  selectedChapterBg:   '#34495E',
  selectedChapterText: '#ffffff',
  verseCountBg:        '#F5F5F5',
  verseCountText:      '#333333',

  primaryText:         '#333333',
  secondaryText:       '#999999',
  accentText:          '#16A085',
  faIconColor:         '#2C3E50',

  optionDotsColor:     '#BEBEBE',
  hrColor:             '#eeeeee',
  transNameColor:      '#999999',
  transTextColor:      '#333333',
  selectedVerseBg:     '#fffde7',
  arabicTextColor:     '#1a1a1a',

  footerBg:            '#e5e5e5',
  footerText:          '#999999',
  footerBorderColor:   '#D0D0D0',

  settingsSeparator:   '#e8e8e8',
  accordionHeader:     '#16A085',
  accordionBorder:     '#e8e8e8',
  settingsLabelText:   '#333333',
  switchTrackActive:   '#1ABC9C',
  switchThumb:         '#009587',

  recitationBg:        '#1ABC9C',
  repeatBadgeBg:       '#2C3E50',
  recitationCtrlIcon:  '#2C3E50',
  seekTrackBg:         'rgba(44,62,80,0.25)',
  seekFillBg:          'rgba(44,62,80,0.55)',
  playBtnBg:           'rgba(44,62,80,0.2)',

  readingIndicator:    '#2C3E50',

  interactiveColor:    '#1ABC9C',
  sliderTrackBg:       '#ccc',
  switchThumbInactive: '#f4f3f4',
  selectedOptionBg:    '#f0f9f7',

  overlayBg:           'rgba(0,0,0,0.4)',
  glowText:            '#0F846D',
  inputBg:             '#1ABC9C',
};

export const DarkColors: ThemeColors = {
  centerPaneBg:        '#666666',
  leftPaneBg:          '#666666',
  rightPaneBg:         '#525252',

  topBarBg:            '#2C3E50',
  topBarIcon:          '#ECF0F1',

  cardBg:              '#2F2F2F',
  cardHasFootnotes:    '#FBB917',

  chapterItemBg:       '#2F2F2F',
  chapterNameText:     '#ECF0F1',
  chapterTransText:    '#AAAAAA',
  selectedChapterBg:   '#1E2A35',
  selectedChapterText: '#ffffff',
  verseCountBg:        '#555555',
  verseCountText:      '#ffffff',

  primaryText:         '#ECF0F1',
  secondaryText:       '#AAAAAA',
  accentText:          '#ECF0F1',
  faIconColor:         '#F5F5F5',

  optionDotsColor:     '#808080',
  hrColor:             '#454545',
  transNameColor:      '#AAAAAA',
  transTextColor:      '#ECF0F1',
  selectedVerseBg:     '#1E2A35',
  arabicTextColor:     '#ECF0F1',

  footerBg:            '#666666',
  footerText:          '#AAAAAA',
  footerBorderColor:   '#555555',

  settingsSeparator:   '#3a3a3a',
  accordionHeader:     '#ffffff',
  accordionBorder:     '#333333',
  settingsLabelText:   '#ECF0F1',
  switchTrackActive:   '#1ABC9C',
  switchThumb:         '#193046',

  recitationBg:        '#2C3E50',
  repeatBadgeBg:       '#1e2a35',
  recitationCtrlIcon:  '#ECF0F1',
  seekTrackBg:         'rgba(236,240,241,0.3)',
  seekFillBg:          'rgba(236,240,241,0.65)',
  playBtnBg:           'rgba(236,240,241,0.15)',

  readingIndicator:    '#1ABC9C',

  overlayBg:           'rgba(0,0,0,0.6)',
  interactiveColor:    '#2C3E50',
  sliderTrackBg:       '#ccc',
  switchThumbInactive: '#666666',
  selectedOptionBg:    '#1E2A35',

  glowText:            '#FBB917',
  inputBg:             '#34495E',
};

/** Resolves the effective theme ('light' | 'dark') from settings. */
export function resolveTheme(
  themeValue: string,
  autoSwitch: boolean,
): 'light' | 'dark' {
  if (autoSwitch && themeValue !== 'system') {
    const hour = new Date().getHours();
    return hour >= 6 && hour < 18 ? 'light' : 'dark';
  }
  if (themeValue === 'system') {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }
  return themeValue === 'dark' ? 'dark' : 'light';
}

/** Hook — returns the resolved color palette and a convenience isDark flag. */
export function useTheme(): { colors: ThemeColors; isDark: boolean } {
  const themeValue  = useAppStore((s) => s.settings.theme.value);
  const autoSwitch  = useAppStore((s) => s.settings.auto_switch_theme);
  const resolved    = resolveTheme(themeValue, autoSwitch);
  const isDark      = resolved === 'dark';
  return { colors: isDark ? DarkColors : LightColors, isDark };
}
